import { useEffect, useState } from 'react';
import { Dialog, Portal, CloseButton, Box, Button, Field, Input, Text, VStack, Spinner } from '@chakra-ui/react';
import { QRCodeSVG } from 'qrcode.react';
import { useTranslation } from 'react-i18next';
import AccountService from '@/services/AccountService';
import { toaster } from '@/components/ui/toaster';

const SetupTwoFactorDialog = ({ isOpen, onClose, onEnabled }) => {
    const { t } = useTranslation('account');
    const [isLoadingSetup, setIsLoadingSetup] = useState(false);
    const [secret, setSecret] = useState('');
    const [otpAuthUri, setOtpAuthUri] = useState('');
    const [code, setCode] = useState('');
    const [error, setError] = useState('');
    const [isConfirming, setIsConfirming] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        setCode('');
        setError('');
        setIsLoadingSetup(true);
        AccountService.setupTwoFactor()
            .then((result) => {
                setSecret(result.secret);
                setOtpAuthUri(result.otpAuthUri);
            })
            .catch(() => {
                setError(t('settingsPage.twoFactor.setupLoadError'));
            })
            .finally(() => setIsLoadingSetup(false));
    }, [isOpen, t]);

    const handleConfirm = async () => {
        setError('');
        setIsConfirming(true);
        try {
            await AccountService.confirmTwoFactor(code);
            toaster.create({ type: 'success', title: t('settingsPage.twoFactor.toastEnabled') });
            onEnabled();
            onClose();
        } catch (err) {
            setError(err?.response?.status === 400
                ? t('settingsPage.twoFactor.errorInvalidCode')
                : t('settingsPage.twoFactor.toastError'));
        } finally {
            setIsConfirming(false);
        }
    };

    return (
        <Dialog.Root open={isOpen} onOpenChange={(e) => !e.open && onClose()}>
            <Portal>
                <Dialog.Backdrop />
                <Dialog.Positioner p={4}>
                    <Dialog.Content bg="bg.card" color="white" border="1px solid" borderColor="border.primary" w={{ base: 'calc(100vw - 2rem)', md: 'md' }} maxW="100%">
                        <Dialog.Body pt={6} pb={6} px={6}>
                            <Text fontWeight="bold" fontSize="lg" mb={4}>{t('settingsPage.twoFactor.setupTitle')}</Text>

                            {isLoadingSetup ? (
                                <VStack py={8}><Spinner color="brand.400" size="lg" /></VStack>
                            ) : (
                                <VStack gap={4} align="stretch">
                                    <Text color="text.grey" fontSize="sm">{t('settingsPage.twoFactor.setupIntro')}</Text>

                                    {otpAuthUri && (
                                        <Box bg="white" p={3} borderRadius={8} alignSelf="center">
                                            <QRCodeSVG value={otpAuthUri} size={180} />
                                        </Box>
                                    )}

                                    <Box>
                                        <Text color="text.grey" fontSize="xs" mb={1}>{t('settingsPage.twoFactor.manualEntryLabel')}</Text>
                                        <Text color="white" fontSize="sm" fontFamily="mono" wordBreak="break-all">{secret}</Text>
                                    </Box>

                                    <Field.Root required>
                                        <Field.Label color="white">{t('settingsPage.twoFactor.codeLabel')} <Field.RequiredIndicator /></Field.Label>
                                        <Input
                                            variant="primary"
                                            inputMode="numeric"
                                            autoComplete="one-time-code"
                                            value={code}
                                            onChange={(e) => setCode(e.target.value)}
                                        />
                                    </Field.Root>

                                    {error && <Text color="red.400" fontSize="sm">{error}</Text>}

                                    <Button variant="primary" onClick={handleConfirm} loading={isConfirming} disabled={!code}>
                                        {t('settingsPage.twoFactor.confirmButton')}
                                    </Button>
                                </VStack>
                            )}
                        </Dialog.Body>
                        <Dialog.CloseTrigger asChild>
                            <CloseButton size="sm" position="absolute" top="2" right="2" />
                        </Dialog.CloseTrigger>
                    </Dialog.Content>
                </Dialog.Positioner>
            </Portal>
        </Dialog.Root>
    );
};

export default SetupTwoFactorDialog;
